import React, { useCallback, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import { ConfirmationModal } from "src/components";
import { securityOptions } from "src/shared/config/storeConfig";
import { anonymizeFullStorySession } from "src/shared/utilities/fullStorySession";
import {
  stashAuthReturnTo,
  shouldSkipAuthReturnTo,
} from "src/shared/utilities/authRedirect";
import { idleLogoutReturnPath } from "src/shared/utilities/idleSession";
import { useIdleSessionLogout } from "src/shared/utilities/useIdleSessionLogout";

/**
 * Proactive idle logout for Auth0 sessions (skips ops correction / login-as).
 * Warns ~1 min before logging out; lands on /error/401?reason=idle with returnTo
 * stashed so Sign In resumes the application.
 */
export const IdleSessionGuard = () => {
  const { isAuthenticated, isLoading, logout } = useAuth0();
  const location = useLocation();
  const loggingOutRef = useRef(false);

  const enabled =
    !isLoading &&
    isAuthenticated &&
    !securityOptions.hasOpsGrant() &&
    !shouldSkipAuthReturnTo(`${location.pathname}${location.search}`);

  const handleExpire = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;

    const returnTo = `${window.location.pathname}${window.location.search}`;
    stashAuthReturnTo(returnTo);
    try {
      sessionStorage.setItem("tp_logout_reason", "idle");
    } catch {
      // ignore
    }
    anonymizeFullStorySession();

    const landing = `${window.location.origin}${idleLogoutReturnPath()}`;
    void logout({
      logoutParams: {
        returnTo: landing,
      },
    });
  }, [logout]);

  const { isWarning, staySignedIn } = useIdleSessionLogout({
    enabled,
    onExpire: handleExpire,
  });

  if (!isWarning) return null;

  return (
    <ConfirmationModal
      isYesNoConfirmationModal
      hideClose
      title="Still working on your application?"
      content="You'll be signed out in about a minute due to inactivity. Click Continue to keep working."
      btnTitle="Continue"
      cancelBtnTitle="Sign out"
      onFormSubmit={staySignedIn}
      onClickCancelEvent={handleExpire}
      onCloseEvent={staySignedIn}
    />
  );
};
